// Version lists and per-ref content fetching for the compare view (RES-561).
//
// A "ref" identifies one side's version as a string, deep-linkable via query params:
//   current        — the live draft (/retrievev2)
//   pub:<docId>    — a published document (publish_dto, purge-proof)
//   ver:<id>       — a ticker_data_version snapshot (drafts are purged after ~5 days)
import { format, parseISO } from 'date-fns';

// Base64 tickers may contain '/' or '+' (comma lists), template names contain spaces.
const seg = (value) => encodeURIComponent(value);

// Route to the compare view for a tickerKey + template. Both segments are encoded:
// Base64 tickerKeys can contain '/', '+' and '=', template names contain spaces —
// ComparePage decodes them back (React Router v5 leaves path params encoded).
export function comparePath(tickerKey, templateName) {
	return `/compare/${encodeURIComponent(btoa(tickerKey))}/${encodeURIComponent(templateName)}`;
}

// The tickerKey the /versions endpoints identify a document by, from the Data
// controller's fields (mirrors DataController.getVersionBase).
export function compareTickerKey(data) {
	if (!data)
		return null;
	let identifier = data.tickers && data.tickers.length ? data.tickers.join(',') : null;
	if (data.templateType === 'selection' || data.templateType === 'scheme')
		identifier = data.tickerGroup;
	return identifier || null;
}

// Compare link for one published document out of the library's doc list: the
// clicked (newer) doc lands on the LEFT (follow-up item 3), compared against the
// previous published document of the same ticker & template when one exists, else
// against the current draft (which is then the newer side and takes the left).
// The whole list travels in location.state so the compare header's Previous/Next
// Doc buttons can iterate it.
export function compareLinkForDoc(docs, index) {
	const doc = docs && docs[index];
	if (!doc || !doc.docId || !doc.ticker || !doc.type)
		return null;
	const series = docs
		.filter((d) => d.ticker === doc.ticker && d.type === doc.type && d.docId)
		.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
	const pos = series.findIndex((d) => d.docId === doc.docId);
	const prev = pos >= 0 ? series[pos + 1] : null;
	return {
		pathname: comparePath(doc.ticker, doc.type),
		search: prev
			? `?left=pub:${doc.docId}&right=pub:${prev.docId}`
			: `?left=current&right=pub:${doc.docId}`,
		state: { compareDocs: docs, compareIndex: index },
	};
}

// Deep link from the compare view back to the editor (feedback item 8,
// plan-compareDocuments.md §3.12 D). Mirrors the param shapes handleEditorURLParams
// writes and EditorSideBarExtension restores on mount, keyed by the template's
// ticker type. tickerKey is the compare route's document identity
// (compareTickerKey): the ticker list for open, the group name for selection, the
// offer number for scheme, and the fund number ALONE for fund — so fund links need
// offerNumber resolved separately (only ?offer= selects the editor's scheme
// dropdown), and degrade to ?fund= without it. Scheme-selection's tickerKey is the
// DOCUMENT selection value (the editor sets tickers = [URLSchemeSelection]); its
// offer is reverse-looked-up from the template's documentSelectionOptions
// (offer → [{name, value}], values de-facto unique — a value under two offers
// would already collide in version storage). Fixed, unknown types, and any
// unresolvable lookup degrade to template-only — the editor opens on the right
// template and the user re-picks the asset; the link is never dead. Returns a
// location object for history.push, or null with no template.
export function editorPathForDocument({ templateName, tickerType, tickerKey, offerNumber, documentSelectionOptions }) {
	if (!templateName)
		return null;
	const params = new URLSearchParams();
	params.set('template', templateName);
	if (tickerKey) {
		if (tickerType === 'open' || tickerType === 'selection')
			params.set('asset', tickerKey);
		else if (tickerType === 'scheme')
			params.set('scheme', tickerKey);
		else if (tickerType === 'brand')
			params.set('brand', tickerKey);
		else if (tickerType === 'fund') {
			if (offerNumber)
				params.set('offer', offerNumber);
			params.set('fund', tickerKey);
		}
		else if (tickerType === 'scheme-selection') {
			const byOffer = documentSelectionOptions || {};
			const offers = Object.keys(byOffer).filter((key) => byOffer[key]?.some((opt) => opt?.value === tickerKey));
			// A bare ?schemeSelection= restores nothing (the editor's SCHEME_SELECTION
			// branch is gated on URLScheme), so stay template-only without the offer —
			// and on an AMBIGUOUS value (listed under two offers; nothing enforces
			// uniqueness) rather than guess which scheme the author meant.
			if (offers.length === 1) {
				params.set('scheme', offers[0]);
				params.set('schemeSelection', tickerKey);
			}
		}
	}
	return { pathname: '/editor', search: '?' + params.toString() };
}

export function parseRef(str) {
	if (!str)
		return null;
	if (str === 'current')
		return { kind: 'current' };
	const match = /^(pub|ver):(\d+)$/.exec(str);
	return match ? { kind: match[1], id: parseInt(match[2], 10) } : null;
}

// The single front-end predicate for "may use compare" (follow-up item 1):
// EDIT only — external managers (EXTERNAL_EDITOR without EDIT) get no compare
// entry points. Must stay in step with the /compare route guard in
// InternalApiApp (requiredFeature="EDIT"); SideBarRight's !isExternalManager
// gate is equivalent inside the editor (only reachable with EDIT or
// EXTERNAL_EDITOR). Change the policy HERE and re-check those two.
export function canUseCompare(me) {
	return !!(me && me.perms && me.perms.includes('EDIT'));
}

// True when a deep link predates the item-3 swap, i.e. was minted with the
// NEWER document on the right: until 2026-08-03 the app generated
// ?left=<pub|ver>&right=current and ?left=pub:<older>&right=pub:<newer>.
// Rendering such a link unswapped would draw every diff mark inverted, so
// ComparePage normalises it once on arrival. `published` is
// fetchCompareOptions' newest-first list (dates the pub-vs-pub case);
// unknown doc ids → not treated as legacy.
export function isLegacyOrientation(leftRef, rightRef, published) {
	const left = parseRef(leftRef);
	const right = parseRef(rightRef);
	if (!left || !right)
		return false;
	// current is always the newest version — it can only correctly sit left.
	if (right.kind === 'current' && left.kind !== 'current')
		return true;
	if (left.kind === 'pub' && right.kind === 'pub') {
		const pos = (id) => (published || []).findIndex((d) => d.docId === id);
		const leftPos = pos(left.id);
		const rightPos = pos(right.id);
		// Newest-first list: a larger index is an older publish.
		return leftPos >= 0 && rightPos >= 0 && leftPos > rightPos;
	}
	return false;
}

function safeDate(iso, pattern) {
	try {
		return format(parseISO(iso), pattern);
	} catch (e) {
		return iso || '';
	}
}

// "vN.M" for a numbered version, or null for an unnumbered one (item 5). The single
// place that absorbs BOTH backend conventions for "unnumbered": /documents/list
// translates the stored 0 to an absent major (null), while /versions returns the raw
// 0/0 column defaults. Either way the row renders with no prefix — never "v0.0".
// A 0 major with a real minor is legitimate: pre-first-publish drafts (v0.1, v0.2, …).
export function versionNumberLabel(major, minor) {
	if (typeof major !== 'number' || typeof minor !== 'number')
		return null;
	if (major === 0 && minor === 0)
		return null;
	return `v${major}.${minor}`;
}

// "vN.0" for a published document summary (/documents/list shape), or null when it
// is unnumbered (null major). A publish is by definition the ".0" of its major —
// every consumer of a published doc's number (dropdown labels, the compare-header
// badge) must come through here so the convention lives in one place.
export function publishedVersionLabel(doc) {
	return doc ? versionNumberLabel(doc.major, 0) : null;
}

// "vN.M — {label}" when numbered, bare label when not — the ONE format for putting a
// version number in front of display text (compare dropdowns, History sidebar rows).
export function withVersionPrefix(number, label) {
	return number ? `${number} — ${label}` : label;
}

// Short label for space-constrained renderings of a compare option — the CLOSED
// select value and the comments-panel side tabs (2026-08-17 feedback item 4,
// plan-compareDocuments.md §3.12 D): draft/version number only. The OPEN dropdown menu
// keeps the full label (VersionSelect's formatOptionLabel) so same-day drafts stay
// distinguishable. Unnumbered rows fall back to their dated form; an option built
// without its doc/version payload falls back to the full label rather than lying.
export function compareOptionShortLabel(option) {
	if (!option)
		return null;
	if (option.kind === 'pub') {
		const number = publishedVersionLabel(option.doc);
		if (number)
			return number;
		return option.doc && option.doc.date ? `Published ${safeDate(option.doc.date, 'dd/MM/yyyy')}` : option.label;
	}
	if (option.kind === 'ver') {
		const v = option.version;
		const number = v ? versionNumberLabel(v.major, v.minor) : null;
		if (number)
			return number;
		return v && v.savedTime ? `Draft ${safeDate(v.savedTime, 'dd/MM/yyyy')}` : option.label;
	}
	// current (already just "Current draft") and anything unrecognised
	return option.label;
}

// react-select's default filter searches option.label, which no longer carries the
// words "Published"/"Draft" (2026-08-20: status is inferred from the version number,
// ".0" = published). Fold the status word for the option's kind back into the search
// haystack so typing "draft" or "published" still narrows the searchable dropdown.
export function compareOptionSearchFilter(candidate, input) {
	if (!input)
		return true;
	const kind = candidate.data && candidate.data.kind;
	const statusWord = kind === 'pub' ? 'published' : kind === 'ver' ? 'draft' : '';
	return `${candidate.label || ''} ${statusWord}`.toLowerCase().includes(input.toLowerCase());
}

// The author the compare header shows: the LEFT pane's document author (2026-08-17
// feedback item 5 — the header used to show focalDoc's author, which could be the
// RIGHT pane's published doc when the left held the current draft).
//   pub:<id>  → the published doc summary's author
//   ver:<id>  → that snapshot's saver
//   current   → the most recent save's user (best available proxy for the draft's
//               author — /retrievev2 carries no authorship). Sorted here, not taken
//               from endpoint order.
export function authorForRef(refString, { published, versions }) {
	const ref = parseRef(refString);
	if (!ref)
		return null;
	if (ref.kind === 'pub') {
		const doc = (published || []).find((d) => d.docId === ref.id);
		return (doc && doc.author) || null;
	}
	if (ref.kind === 'ver') {
		const row = (versions || []).find((v) => v.id === ref.id);
		return (row && row.user) || null;
	}
	const newest = (versions || []).reduce(
		(best, v) => (!best || (v.savedTime || '').localeCompare(best.savedTime || '') > 0 ? v : best),
		null
	);
	return (newest && newest.user) || null;
}

// Builds the shared dropdown option list for both panes.
// Published docs are the canonical, purge-proof entries, so draft versions that were
// published (publishedDocument > 0) are dropped in favour of their pub: option.
export function fetchCompareOptions(remote, { b64Ticker, templateName, ticker }) {
	const docsPromise = remote.get(`/documents/list?ticker=${encodeURIComponent(ticker)}&num=200`)
		.catch(() => []);
	const versionsPromise = remote.get(`/versions/${seg(b64Ticker)}/${seg(templateName)}?limit=30`)
		.then((resp) => (resp && resp.success ? resp.data : []))
		.catch(() => []);
	return Promise.all([docsPromise, versionsPromise]).then(([docs, versions]) => {
		const published = (docs || [])
			.filter((d) => d.type === templateName && d.docId)
			.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
		const drafts = (versions || []).filter((v) => !(v.publishedDocument > 0));
		// A published doc's number rides on the doc summary because fetchCompareOptions
		// drops the locked publish snapshot from the drafts list (publishedDocument > 0)
		// in favour of this pub: entry.
		const options = [
			{ value: 'current', kind: 'current', label: 'Current draft' },
			// Label shape: "vN.M — {title|author} — {date}". Published/draft is inferred
			// from the version number (".0" = published), so the status word only appears
			// on unnumbered legacy rows, where nothing else carries it.
			...published.map((d) => {
				const number = publishedVersionLabel(d);
				const detail = `${d.title ? d.title + ' — ' : ''}${number ? '' : 'Published '}${safeDate(d.date, 'dd/MM/yyyy')}`;
				return { value: 'pub:' + d.docId, kind: 'pub', label: withVersionPrefix(number, detail), doc: d };
			}),
			...drafts.map((v) => {
				const number = versionNumberLabel(v.major, v.minor);
				const detail = `${v.user ? v.user + ' — ' : ''}${number ? '' : 'Draft '}${safeDate(v.savedTime, 'dd/MM/yyyy, hh:mm a')}`;
				return { value: 'ver:' + v.id, kind: 'ver', label: withVersionPrefix(number, detail), version: v };
			}),
		];
		return { options, published, versions: versions || [] };
	});
}

// Default pair: left = current draft (the newer document sits on the LEFT since
// follow-up item 3), right = latest published version as the baseline; a never-
// published document falls back to the latest draft save; neither → empty state.
export function defaultRefs(options) {
	const firstPub = options.find((o) => o.kind === 'pub');
	const firstVer = options.find((o) => o.kind === 'ver');
	return {
		left: 'current',
		right: firstPub ? firstPub.value : firstVer ? firstVer.value : null,
	};
}

// Resolves a ref to its content map {dataName: json string}. Always resolves
// (never rejects) with {status, data?, message?}:
//   ok           — data is the content map
//   purged       — a ver: row deleted by the 5-day purge (caller refreshes options)
//   unavailable  — content that can't be produced (legacy format / null publish_dto)
//   error        — transport or unexpected failures
export function fetchContentForRef(remote, refString, { b64Ticker, templateName, versions }) {
	const ref = parseRef(refString);
	if (!ref)
		return Promise.resolve({ status: 'error', message: 'Unrecognised version reference' });
	if (ref.kind === 'current') {
		return remote.get(`/retrievev2/${seg(b64Ticker)}/${seg(templateName)}`)
			.then((data) => ({ status: 'ok', data }))
			.catch(() => ({ status: 'error', message: 'Unable to load the current draft' }));
	}
	if (ref.kind === 'ver')
		return fetchVersionData(remote, ref.id, { b64Ticker, templateName });
	// pub: publish_dto is authoritative; old rows predating publish_dto fall back to
	// the locked version snapshot that produced the publish, if one survives.
	const lockedFallback = () => {
		const locked = (versions || []).find((v) => v.publishedDocument === ref.id);
		if (locked)
			return fetchVersionData(remote, locked.id, { b64Ticker, templateName });
		return { status: 'unavailable', message: 'The stored content for this published document is no longer available' };
	};
	return remote.get(`/documents/document/${ref.id}/publishdto`)
		.then((dto) => (dto && dto.data ? { status: 'ok', data: dto.data, dto } : lockedFallback()))
		.catch((err) => {
			// A null publish_dto arrives as HTTP 200 with an EMPTY body, which the
			// remote's resp.json() rejects with a SyntaxError rather than resolving
			// null. ONLY that exact shape means "no stored dto" — numeric rejections
			// are HTTP failures and anything else (e.g. TypeError from a network
			// outage) is a transport error, not a permanently-missing document.
			if (err instanceof SyntaxError)
				return lockedFallback();
			return { status: 'error', message: 'Unable to load this published document' };
		});
}

// The review round that produced a published document, for round-scoped comments
// (plan D5 — comments are round-scoped, not version-scoped). The endpoint returns
// {} for legacy publishes that predate workflow_published_document_link.
//   ok    — workflowStatusId identifies the round
//   none  — no round recorded (legacy publish)
//   error — transport failure
export function fetchWorkflowStatusId(remote, docId) {
	return remote.get(`/documents/document/${docId}/workflow-status`)
		.then((resp) => (resp && resp.workflowStatusId
			? { status: 'ok', workflowStatusId: resp.workflowStatusId }
			: { status: 'none' }))
		.catch(() => ({ status: 'error' }));
}

function fetchVersionData(remote, versionId, { b64Ticker, templateName }) {
	return remote.get(`/versions/${seg(b64Ticker)}/${seg(templateName)}/${versionId}/data`)
		.then((data) => ({ status: 'ok', data }))
		.catch((status) => {
			if (status === 404)
				return { status: 'purged', message: 'This saved version has been purged and can no longer be compared' };
			if (status === 422)
				return { status: 'unavailable', message: "This version's saved format can't be compared" };
			return { status: 'error', message: 'Unable to load this saved version' };
		});
}
